import type {
  AgCartesianChartOptions,
  AgCartesianSeriesOptions,
  AgChartLegendClickEvent,
  AgChartLegendDoubleClickEvent
} from "ag-charts-enterprise";
import { useCallback, useMemo, useState } from "react";
import type { ForecastTableControlValues } from "../../../components/tableControls/forecastTableControlValues";
import type { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import {
  chartDateToPeriodKey,
  formatTimeTick,
  getAxisInterval,
  getAxisMax,
  NORMALIZED_AXIS_START
} from "../../../utils/chartDateUtils";
import {
  COLOR_MAP,
  type EventCalendarEntry,
  type EventChartDataPoint,
  FALLBACK_COLOR,
  TYPE_LABELS
} from "../eventCalendarConstants";

/**
 * Normalizes a date to the 2000/2001 base year so multiple fiscal years
 * can overlay on a single chart axis. Week 32+ stays in 2000; earlier weeks
 * advance to 2001 to avoid a December–January gap.
 */
function toChartDate(date: string): Date {
  const parsedDate = new Date(date);
  const normalizedDate = new Date(
    NORMALIZED_AXIS_START.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate()
  );
  return normalizedDate < NORMALIZED_AXIS_START
    ? new Date(
        NORMALIZED_AXIS_START.getFullYear() + 1,
        parsedDate.getMonth(),
        parsedDate.getDate()
      )
    : normalizedDate;
}

function buildSeriesFromEvents(
  events: EventCalendarEntry[],
  hiddenTypes: Set<string>
): AgCartesianSeriesOptions[] {
  const seenTypes = new Set<string>();

  return events.map((event) => {
    const type = event.activation_type.toLowerCase();
    const color = COLOR_MAP[type] ?? FALLBACK_COLOR;
    const datum: EventChartDataPoint = {
      fiscalYear: event.fiscal_year,
      activationType: event.activation_type,
      activationName: event.activation_name,
      dateFrom: event.date_from,
      dateTo: event.date_to,
      chartDateFrom: toChartDate(event.date_from),
      chartDateTo: toChartDate(event.date_to)
    };
    const isFirstOfType = !seenTypes.has(event.activation_type);
    seenTypes.add(event.activation_type);
    return {
      type: "range-bar",
      direction: "horizontal",
      grouped: false,
      data: [datum],
      xKey: "fiscalYear",
      yLowKey: "chartDateFrom",
      yHighKey: "chartDateTo",
      yName: TYPE_LABELS[type] ?? event.activation_type,
      legendItemName: TYPE_LABELS[type] ?? event.activation_type,
      showInLegend: isFirstOfType,
      visible: !hiddenTypes.has(TYPE_LABELS[type] ?? event.activation_type),
      fill: color,
      stroke: color,
      fillOpacity: 0.6,
      width: 20,
      tooltip: {
        renderer: ({ datum }: { datum: EventChartDataPoint }) => ({
          heading: "",
          title: datum.activationName,
          data: [
            { label: "Type", value: datum.activationType },
            { label: "Fiscal Year", value: datum.fiscalYear },
            { label: "From", value: datum.dateFrom },
            { label: "To", value: datum.dateTo }
          ]
        })
      }
    };
  });
}

function sortEventsByType(events: EventCalendarEntry[]): EventCalendarEntry[] {
  const typeOrder = Object.keys(TYPE_LABELS);
  return [...events].sort((a, b) => {
    const aIdx = typeOrder.indexOf(a.activation_type.toLowerCase());
    const bIdx = typeOrder.indexOf(b.activation_type.toLowerCase());
    const typeDiff =
      (aIdx === -1 ? typeOrder.length : aIdx) -
      (bIdx === -1 ? typeOrder.length : bIdx);
    return typeDiff !== 0
      ? typeDiff
      : a.fiscal_year.localeCompare(b.fiscal_year);
  });
}

function formatTimeAxisLabel(
  value: Date,
  timePeriod: ForecastTableControlValues["timePeriod"]
): string | undefined {
  switch (timePeriod) {
    case "MO":
      return value.toLocaleDateString("en-GB", { month: "short" }).slice(0, 3);
    case "QTR": {
      const monthOffset =
        (value.getMonth() - NORMALIZED_AXIS_START.getMonth() + 12) % 12;
      return `Q${Math.floor(monthOffset / 3) + 1}`;
    }
    case "HY": {
      const monthOffset =
        (value.getMonth() - NORMALIZED_AXIS_START.getMonth() + 12) % 12;
      return `H${Math.floor(monthOffset / 6) + 1}`;
    }
    case "WK": {
      const key = chartDateToPeriodKey(value, "WK");
      return key ?? "";
    }
  }
}

export function useEventCalendarOptions(
  events: EventCalendarEntry[] | undefined,
  metaData: ForecastMetaDataType | undefined,
  timePeriod: ForecastTableControlValues["timePeriod"],
  weekLabels: ForecastTableControlValues["weekLabels"]
): AgCartesianChartOptions {
  // To mimic behaviour plotly graph and have different activation types in the same fiscal year stack,
  // we need to implement custom seen and hidden items list as well as legend item clicks.
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());

  const allTypesInData = useMemo(
    () => [
      ...new Set(
        (events ?? []).map(
          (e) => TYPE_LABELS[e.activation_type] ?? e.activation_type
        )
      )
    ],
    [events]
  );

  const handleLegendItemClick = useCallback(
    (event: AgChartLegendClickEvent) => {
      event.preventDefault();
      setHiddenTypes((prev) => {
        const next = new Set(prev);
        if (next.has(event.text)) next.delete(event.text);
        else next.add(event.text);
        return next;
      });
    },
    []
  );

  const handleLegendItemDoubleClick = useCallback(
    (event: AgChartLegendDoubleClickEvent) => {
      event.preventDefault();
      setHiddenTypes((prev) => {
        const otherTypes = allTypesInData.filter((t) => t !== event.text);
        const alreadyIsolated = otherTypes.every((t) => prev.has(t));
        return alreadyIsolated ? new Set() : new Set(otherTypes);
      });
    },
    [allTypesInData]
  );

  const gregMapping = metaData?.gregMapping;

  return useMemo(() => {
    if (!events || events.length === 0 || timePeriod === "PTG") {
      return { series: [] } as AgCartesianChartOptions;
    }

    // Sort events by activation type using keys order from TYPE_LABELS,
    // then by fiscal year for consistent stacking of bars in the same year.
    const sorted = sortEventsByType(events);
    const series = buildSeriesFromEvents(sorted, hiddenTypes);

    return {
      minHeight: 0,
      height: 200,
      series,
      axes: {
        x: {
          type: "category",
          position: "left",
          crosshair: { enabled: false },
          reverse: true
        },
        y: {
          type: "time",
          position: "bottom",
          crosshair: { enabled: false },
          min: NORMALIZED_AXIS_START,
          max: getAxisMax(),
          interval: getAxisInterval(timePeriod),
          label: {
            formatter: ({ value }: { value: Date }) =>
              formatTimeTick(value, timePeriod, weekLabels, gregMapping)
          }
        }
      },
      formatter: {
        y(params) {
          if (params.type !== "date") return;
          const { value } = params;
          return formatTimeAxisLabel(value, timePeriod);
        }
      },
      legend: {
        position: "top",
        item: { padding: { left: 24, right: 24 } },
        listeners: {
          legendItemClick: handleLegendItemClick,
          legendItemDoubleClick: handleLegendItemDoubleClick
        }
      },
      tooltip: { range: "exact" }
    } satisfies AgCartesianChartOptions;
  }, [
    events,
    gregMapping,
    timePeriod,
    weekLabels,
    hiddenTypes,
    handleLegendItemClick,
    handleLegendItemDoubleClick
  ]);
}
